import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Hash,
  Lock,
  Unlock,
  Search,
  X,
  ShoppingCart,
  ChevronRight,
  Plus,
  Minus,
  ShoppingBag
} from 'lucide-react';
import api from '../config/api';
import { useCafe } from '../context/CafeContext';
import { useLanguage } from '../context/LanguageContext';
import ItemCard from '../components/ItemCard';
import CounterModal from '../components/CounterModal';
import { showNotification } from '../components/GlobalNotification';
import { getItemImageSrc } from '../utils/itemImage';
import { transliterate } from '../utils/nepaliTransliterate';
import { itemLabel, searchableNames } from '../utils/itemName';

const Dashboard = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const {
    items,
    activeOrders,
    addOrder,
    decreaseOrder,
    refreshItems,
    refreshActiveOrders,
    loading,
    error,
    counterSession,
    currentToken,
    tokenIsStale,
    tokensIssued,
    issueToken,
    ensureToken,
    selectToken
  } = useCafe();
  // The full daily report - the hero needs the cash/bank split and the order
  // count, not just the headline revenue.
  const [todayReport, setTodayReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [counterModalOpen, setCounterModalOpen] = useState(false);
  const [search, setSearch] = useState('');

  const fetchTodayReport = useCallback(async () => {
    try {
      setReportLoading(true);
      const response = await api.get('/api/reports/daily');
      setTodayReport(response.data);
    } catch (reportError) {
      console.error('Error fetching today\'s report:', reportError);
      setTodayReport(null);
    } finally {
      setReportLoading(false);
    }
  }, []);

  // Fetch today's report on component mount
  useEffect(() => {
    fetchTodayReport();
  }, [fetchTodayReport]);

  // Listen for revenue update events from other components
  useEffect(() => {
    const handleRevenueUpdate = () => {
      fetchTodayReport();
    };

    window.addEventListener('revenueUpdate', handleRevenueUpdate);

    return () => {
      window.removeEventListener('revenueUpdate', handleRevenueUpdate);
    };
  }, [fetchTodayReport]);

  // Items and active orders are loaded by CafeContext as soon as the user is
  // authenticated, so there is nothing to kick off here.

  // Filter items to show only those included in menu
  const menuItems = items?.filter(item => item.includeInMenu !== false) || [];

  // Every token still carrying unpaid items - one entry per customer being
  // served. Tokens stay in this list until they are paid, so the cashier can
  // come back to any of them and add another round.
  const openTokens = useMemo(() => {
    const totals = new Map();

    (activeOrders || []).forEach((order) => {
      const token = order.tokenNumber;
      if (!token) {
        return;
      }

      const group = totals.get(token) || { tokenNumber: token, lines: 0, total: 0 };
      group.lines += 1;
      group.total += order.totalAmount || 0;
      totals.set(token, group);
    });

    return Array.from(totals.values()).sort((a, b) => a.tokenNumber - b.tokenNumber);
  }, [activeOrders]);

  // Only the lines on the token being served right now. Item counts on this
  // page have to be this customer's, not a total across every open token -
  // otherwise the minus button takes a unit off somebody else's bill.
  const currentTokenLines = useMemo(
    () => (activeOrders || []).filter(order => order.tokenNumber === currentToken),
    [activeOrders, currentToken]
  );

  const lineForItem = useCallback(
    (itemId) => currentTokenLines.find(order => (order.item?._id || order.item) === itemId),
    [currentTokenLines]
  );

  const quantityOnToken = useCallback(
    (itemId) => lineForItem(itemId)?.quantity || 0,
    [lineForItem]
  );

  // Name search over the menu. Both sections below draw from the filtered list,
  // so a search narrows the token's items and the rest of the menu alike.
  const query = search.trim().toLowerCase();
  // An item named in Devanagari is unreachable from a keyboard that types roman
  // letters, so the query is matched both as typed and transliterated - "momo"
  // has to find मोमो, or naming items in Nepali costs the cashier the search box.
  const nepaliQuery = query ? transliterate(search.trim()).toLowerCase() : '';
  const visibleItems = query
    ? menuItems.filter(item =>
        searchableNames(item).some(
          name =>
            name.includes(query) || (nepaliQuery !== query && name.includes(nepaliQuery))
        )
      )
    : menuItems;

  // Items already on this customer's token, biggest line first
  const itemsWithOrders = visibleItems
    .filter(item => quantityOnToken(item._id) > 0)
    .sort((a, b) => quantityOnToken(b._id) - quantityOnToken(a._id));

  const itemsNotOnToken = visibleItems.filter(item => quantityOnToken(item._id) === 0);

  // Everything sitting on open tokens - what the till is still owed.
  const pendingValue = activeOrders?.reduce((sum, order) => sum + (order.totalAmount || 0), 0) || 0;

  const currentTokenTotal = currentTokenLines.reduce((sum, order) => sum + (order.totalAmount || 0), 0);

  const formatTime = (value) =>
    value ? new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';

  const handleRefresh = async () => {
    try {
      await Promise.all([refreshItems(), refreshActiveOrders(), fetchTodayReport()]);
    } catch (error) {
      console.error('Error refreshing data:', error);
    }
  };

  const handleAddOrder = async (item) => {
    if (!counterSession) {
      showNotification('error', t('Open the counter before taking orders.'));
      return;
    }

    try {
      // A token has to be in the customer's hand before anything goes on it,
      // but tapping an item is enough to start one - the same first tap that
      // starts an order on the mobile app. The number comes back from
      // ensureToken because a token taken just now is not in state yet.
      const token = await ensureToken();
      if (!currentToken) {
        showNotification('success', t('Token #{n} started.', { n: token }));
      }
      await addOrder(item._id, 1, token);
    } catch (addError) {
      showNotification('error', addError.response?.data?.message || addError.message || t('Could not add the item.'));
    }
  };

  const handleIssueToken = async () => {
    // Handing out a second number while the current one is still empty just
    // burns a token and leaves a customer without one
    if (currentToken && currentTokenLines.length === 0) {
      showNotification('info', t('Token #{n} is empty - add items to it first.', { n: currentToken }));
      return;
    }

    try {
      const number = await issueToken();
      showNotification('success', t('Token #{n} started. Add items to it.', { n: number }));
    } catch (tokenError) {
      showNotification('error', tokenError.response?.data?.message || t('Could not create a token.'));
    }
  };

  // Switch back to a customer who is already being served, so the next items
  // go on their existing bill instead of starting a new one
  const handleSelectToken = (tokenNumber) => {
    if (tokenNumber === currentToken) {
      return;
    }
    selectToken(tokenNumber);
    showNotification('info', t('Now adding to token #{n}.', { n: tokenNumber }));
  };

  const handleDecreaseOrder = async (item) => {
    try {
      // Take one unit off this item's line rather than deleting the whole line.
      // Scoped to the current token so this never reaches into another
      // customer's order. Note: order.item is populated, so compare item._id.
      const line = lineForItem(item._id);
      if (!line) {
        showNotification('error', t('{name} is not on token #{n}.', { name: itemLabel(item, language), n: currentToken }));
        return;
      }

      await decreaseOrder(line._id);
    } catch (error) {
      console.error('Error decreasing order:', error);
    }
  };



  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cafe-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-2xl p-4">
        <p className="text-red-800">{t('Error: {message}', { message: error })}</p>
        <button
          onClick={handleRefresh}
          className="mt-2 px-4 py-2 min-h-[44px] bg-red-600 text-white rounded-xl hover:bg-red-700"
        >
          {t('Try Again')}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3 sm:space-y-6">
      <CounterModal isOpen={counterModalOpen} onClose={() => setCounterModalOpen(false)} />

      {/*
        The owner's first question is "how much did we make today?", so the
        figure leads the page - big enough to read at arm's length, with the
        drawer/bank split underneath. Tapping anywhere on the card opens
        Reports. Mirrors the mobile dashboard.
      */}
      <button
        type="button"
        onClick={() => navigate('/reports')}
        className="w-full text-left bg-white rounded-2xl border border-gray-100 shadow-sm p-4 sm:p-5 space-y-3 hover:border-cafe-300 active:bg-gray-50 transition-colors"
      >
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900">{t("Today's sale")}</h2>
          <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0" />
        </div>

        <p
          className={`text-3xl sm:text-4xl font-bold text-cafe-700 leading-tight ${
            reportLoading && !todayReport ? 'animate-pulse' : ''
          }`}
        >
          Rs {(todayReport?.totalRevenue ?? 0).toFixed(2)}
        </p>

        <div className="border-t border-gray-100" />

        {/* Plain columns with thin dividers - the boxed icon tiles that used
            to hold these read as three nested cards instead of one figure. */}
        <div className="grid grid-cols-3 divide-x divide-gray-100">
          <div className="pr-3 min-w-0">
            <p className="text-[11px] sm:text-xs font-semibold text-gray-400">{t('Cash')}</p>
            <p className="text-sm sm:text-lg font-bold text-green-600 truncate">
              Rs {(todayReport?.cashRevenue ?? 0).toFixed(2)}
            </p>
          </div>
          <div className="px-3 min-w-0">
            <p className="text-[11px] sm:text-xs font-semibold text-gray-400">{t('Bank')}</p>
            <p className="text-sm sm:text-lg font-bold text-blue-600 truncate">
              Rs {(todayReport?.bankRevenue ?? 0).toFixed(2)}
            </p>
          </div>
          <div className="pl-3 min-w-0">
            <p className="text-[11px] sm:text-xs font-semibold text-gray-400">{t('Orders')}</p>
            <p className="text-sm sm:text-lg font-bold text-gray-900 truncate">
              {todayReport?.totalOrders ?? 0}
            </p>
          </div>
        </div>
      </button>

      {/* Money still on open tokens. It is the one figure on this page that is
          a to-do rather than a result, so it stays a red tile of its own and
          opens the orders list where those tokens get settled. */}
      <button
        type="button"
        onClick={() => navigate('/orders')}
        className="w-full text-left bg-white rounded-2xl border border-gray-100 shadow-sm p-3 sm:p-4 flex items-center gap-3 hover:border-red-300 active:bg-gray-50 transition-colors"
      >
        <div className="h-10 w-10 sm:h-11 sm:w-11 rounded-xl bg-red-50 text-red-600 flex items-center justify-center flex-shrink-0">
          <ShoppingCart className="h-5 w-5 sm:h-6 sm:w-6" />
        </div>
        <div className="min-w-0">
          <p className="text-[11px] sm:text-xs font-semibold text-gray-400 uppercase tracking-wider">
            {t('Unpaid')}
          </p>
          <p className="text-lg sm:text-2xl font-bold text-red-600 leading-tight truncate">
            Rs {pendingValue.toFixed(2)}
          </p>
        </div>
        <span className="ml-auto flex items-center gap-1 text-xs sm:text-sm font-semibold text-gray-500 flex-shrink-0">
          {t(openTokens.length === 1 ? '{count} token' : '{count} tokens', {
            count: openTokens.length
          })}
          <ChevronRight className="h-5 w-5 text-gray-400" />
        </span>
      </button>

      {/* Counter status and current token */}
      {counterSession ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-3 sm:p-4 space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5 sm:gap-3">
            <div className="flex items-start gap-2.5 min-w-0">
              {/* The green pulse says the till is taking orders; no tinted
                  card needed to say it. */}
              <span className="mt-1.5 h-2.5 w-2.5 rounded-full bg-green-500 flex-shrink-0" aria-hidden="true" />
              <div className="min-w-0">
                <p className="font-semibold text-gray-900">{t('Serving now')}</p>
                {/* The token count is the same figure the mobile app shows,
                    and it decides whether a stored token is still valid. */}
                <p className="text-[11px] sm:text-xs text-gray-500">
                  {t('Opened {time}', { time: formatTime(counterSession.openedAt) })}
                  {counterSession.openedByName
                    ? ` ${t('by {name}', { name: counterSession.openedByName })}`
                    : ''}
                  {' · '}
                  {t(tokensIssued === 1 ? '{count} token issued' : '{count} tokens issued', {
                    count: tokensIssued ?? 0
                  })}
                  {' · '}
                  {/* The only tappable thing in this line of small print, so it
                      is set bolder than the text around it. */}
                  <button
                    type="button"
                    onClick={() => navigate('/counter')}
                    className="font-bold text-cafe-600 hover:text-cafe-700 underline underline-offset-2"
                  >
                    {t('Counter details')}
                  </button>
                </p>
              </div>
            </div>

            {/* Starting a token is the action taken all day, so it gets the
                width; closing the counter happens once and stays an icon */}
            <div className="flex gap-2 flex-shrink-0">
              <button
                onClick={handleIssueToken}
                className="flex-1 sm:flex-none px-4 py-2.5 min-h-[44px] bg-cafe-600 text-white rounded-xl hover:bg-cafe-700 active:scale-[0.98] transition-all font-semibold flex items-center gap-2 justify-center whitespace-nowrap shadow-sm"
              >
                <Hash className="h-4 w-4 flex-shrink-0" />
                {currentToken ? t('New Token') : t('Create Token')}
              </button>
              <button
                onClick={() => setCounterModalOpen(true)}
                aria-label={t('Close counter')}
                title={t('Close counter')}
                className="px-3 sm:px-4 py-2.5 min-h-[44px] min-w-[44px] bg-gray-100 text-gray-600 rounded-xl hover:bg-gray-200 transition-colors font-medium flex items-center gap-2 justify-center whitespace-nowrap"
              >
                <Lock className="h-4 w-4 flex-shrink-0" />
                <span className="hidden sm:inline">{t('Close Counter')}</span>
              </button>
            </div>
          </div>

          {/* The token being built, big enough to call across the counter -
              or, when there is none, how to start one. */}
          {currentToken ? (
            <div className="flex items-baseline gap-2 sm:gap-3">
              <p className="text-2xl sm:text-3xl font-bold text-cafe-700 leading-tight">
                #{currentToken}
              </p>
              <p className="text-sm font-medium text-gray-500 truncate">
                {currentTokenLines.length === 0
                  ? t('nothing on it yet')
                  : `Rs ${currentTokenTotal.toFixed(2)}`}
              </p>
            </div>
          ) : (
            <p className="text-sm text-gray-500">
              {tokenIsStale
                ? t('The token you were on belongs to an earlier session. Tap an item to start a new one.')
                : t('Tap an item to start one, or pick up an open token below.')}
            </p>
          )}

          {/* Every unpaid token stays here until it is paid, so a customer can
              come back for a second round on the same number */}
          {openTokens.length > 0 && (
            <div className="border-t border-gray-100 pt-2.5 sm:pt-3">
              <p className="text-[10px] sm:text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                {t('Open tokens - tap one to add to that customer')}
              </p>
              {/* One swipeable row on phones: a busy counter's token list can
                  never push the menu off the screen */}
              <div className="-mx-3 px-3 sm:mx-0 sm:px-0 overflow-x-auto no-scrollbar">
                <div className="flex gap-2 min-w-max sm:min-w-0 sm:flex-wrap">
                  {openTokens.map((token) => (
                    <button
                      key={token.tokenNumber}
                      onClick={() => handleSelectToken(token.tokenNumber)}
                      className={`px-3.5 py-2 min-h-[44px] rounded-full border text-sm font-medium transition-colors flex items-center gap-1.5 flex-shrink-0 ${
                        token.tokenNumber === currentToken
                          ? 'bg-cafe-600 border-cafe-600 text-white shadow-sm'
                          : 'bg-gray-50 border-gray-200 text-gray-700 hover:border-cafe-400 hover:text-cafe-700 active:bg-cafe-50'
                      }`}
                    >
                      <span className="font-bold">#{token.tokenNumber}</span>
                      <span
                        className={`text-xs whitespace-nowrap ${
                          token.tokenNumber === currentToken ? 'text-cafe-100' : 'text-gray-400'
                        }`}
                      >
                        Rs {token.total.toFixed(2)}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-amber-50 border border-amber-100 rounded-2xl p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2.5 sm:gap-3">
            <div className="h-10 w-10 sm:h-11 sm:w-11 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
              <Lock className="h-5 w-5 sm:h-6 sm:w-6 text-amber-700" />
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-amber-900">{t('The counter is closed')}</p>
              <p className="text-xs sm:text-sm text-amber-800">
                {t('Open it with your starting cash to begin taking orders.')}
              </p>
            </div>
          </div>
          <button
            onClick={() => setCounterModalOpen(true)}
            className="px-4 py-2.5 min-h-[44px] bg-green-600 text-white rounded-xl hover:bg-green-700 active:scale-[0.98] transition-all font-semibold flex items-center gap-2 justify-center flex-shrink-0 shadow-sm"
          >
            <Unlock className="h-4 w-4" />
            {t('Open Counter')}
          </button>
        </div>
      )}

      {/* Menu Items Section. The white panel only appears from sm up - on a
          phone its padding would cost the item grid a third of a column. */}
      <div className="sm:bg-white sm:shadow-sm sm:border sm:border-gray-100 sm:rounded-2xl">
        <div className="sm:p-6">
          {/* Heading, search and count share one row - a search bar on its own
              line would cost the grid a whole row of items on a phone */}
          <div className="flex items-center gap-2 mb-3 sm:mb-6">
            <h3 className="text-base sm:text-lg leading-6 font-semibold text-gray-900 flex-shrink-0">
              {t('Menu')}
            </h3>

            <div className="relative flex-1 min-w-0 sm:max-w-xs sm:ml-auto sm:flex-none sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('Search items')}
                aria-label={t('Search menu items')}
                className="w-full h-10 pl-9 pr-8 text-sm border border-gray-200 rounded-xl bg-white sm:bg-gray-50 sm:focus:bg-white focus:outline-none focus:ring-2 focus:ring-cafe-500 focus:border-transparent transition-colors"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  aria-label={t('Clear search')}
                  className="absolute right-1 top-1/2 -translate-y-1/2 p-1.5 text-gray-400 hover:text-gray-600 rounded-md"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            <span className="hidden sm:inline text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full whitespace-nowrap flex-shrink-0">
              {query
                ? t('{shown} of {total}', { shown: visibleItems.length, total: menuItems.length })
                : t('{count} items', { count: menuItems.length })}
            </span>
          </div>

          {menuItems && menuItems.length > 0 ? (
            visibleItems.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-100 sm:border-0 text-center py-8 px-4">
                <div className="h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <h3 className="text-base font-medium text-gray-900 mb-1">
                  {t('Nothing matches "{query}"', { query: search.trim() })}
                </h3>
                <button
                  onClick={() => setSearch('')}
                  className="mt-2 text-sm font-medium text-cafe-600 hover:text-cafe-700"
                >
                  {t('Clear search')}
                </button>
              </div>
            ) : (
            <div className="space-y-4 sm:space-y-6">
              {/* Items already on this customer's token */}
              {itemsWithOrders.length > 0 && (
                /*
                  A tinted tray rather than another run of cards under a small
                  heading. Both sections used the same tiny uppercase label and
                  differed only in text colour, so on a phone the customer's
                  order and the menu below read as one continuous grid. The
                  banded background is what actually separates them - the page
                  itself is cafe-50, so the tray has to be a step deeper than
                  that to register, and the white cards then lift off it.
                */
                <div className="rounded-2xl bg-cafe-300 border border-cafe-400 p-3 sm:p-4">
                  <div className="flex items-center gap-2.5 mb-3">
                    <span className="flex-shrink-0 inline-flex items-center justify-center bg-cafe-600 text-white text-sm font-bold rounded-full px-2.5 py-1 shadow-sm">
                      #{currentToken}
                    </span>
                    <h4 className="text-base sm:text-lg font-bold text-cafe-900 truncate">
                      {t('On this token')}
                    </h4>
                    <span className="ml-auto flex-shrink-0 text-xs sm:text-sm font-semibold text-cafe-800 bg-white rounded-full px-2.5 py-1">
                      {itemsWithOrders.length} {t(itemsWithOrders.length === 1 ? 'item' : 'items')}
                    </span>
                  </div>
                  {/* Two up on a phone, same as the rest of the menu, so the
                      token's items stay scannable instead of one huge column */}
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
                    {itemsWithOrders.map((item) => (
                      <div
                        key={item._id}
                        /* The tray already groups these, so the heavy 2px border
                           that used to do that job is just noise now. */
                        className="bg-white rounded-2xl border border-cafe-300 shadow-sm overflow-hidden flex flex-col"
                      >
                        <div
                          className="relative h-20 sm:h-28 bg-cafe-50 cursor-pointer flex-shrink-0"
                          onClick={() => handleAddOrder(item)}
                        >
                          {item.image ? (
                            <img
                              src={getItemImageSrc(item.image)}
                              alt={itemLabel(item, language)}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <ShoppingBag className="h-7 w-7 sm:h-8 sm:w-8 text-cafe-300" />
                            </div>
                          )}
                          <span className="absolute top-1.5 right-1.5 min-w-[22px] h-[22px] px-1.5 flex items-center justify-center bg-cafe-600 text-white text-xs font-bold rounded-full shadow-sm">
                            {quantityOnToken(item._id)}
                          </span>
                        </div>

                        <div className="flex-1 p-2.5 sm:p-3.5 flex flex-col">
                          <h4 className="text-sm sm:text-base font-semibold text-gray-900 truncate">
                            {itemLabel(item, language)}
                          </h4>
                          <p className="text-sm font-bold text-cafe-700 mb-2.5">Rs {item.price}</p>

                          {/* Stepper - the count sits between the two targets so
                              a thumb never has to leave the card */}
                          <div className="mt-auto flex items-center gap-1.5">
                            <button
                              onClick={() => handleDecreaseOrder(item)}
                              disabled={quantityOnToken(item._id) === 0}
                              aria-label={t('Remove one {name}', { name: itemLabel(item, language) })}
                              className="h-11 w-11 flex-shrink-0 flex items-center justify-center bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 active:scale-95 disabled:bg-gray-50 disabled:text-gray-300 transition-all"
                            >
                              <Minus className="h-5 w-5" />
                            </button>
                            <span className="flex-1 text-center text-base font-bold text-gray-900">
                              {quantityOnToken(item._id)}
                            </span>
                            <button
                              onClick={() => handleAddOrder(item)}
                              disabled={item.isInventoryItem && item.stock === 0}
                              aria-label={t('Add one {name}', { name: itemLabel(item, language) })}
                              className="h-11 w-11 flex-shrink-0 flex items-center justify-center bg-cafe-600 text-white rounded-xl hover:bg-cafe-700 active:scale-95 disabled:bg-gray-50 disabled:text-gray-300 transition-all shadow-sm"
                            >
                              <Plus className="h-5 w-5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Regular Menu Items */}
              {itemsNotOnToken.length > 0 && (
                <div>
                  {itemsWithOrders.length > 0 && (
                    /* Deliberately quiet: this is the browsing list, and the
                       tray above is the thing being worked on. gray-400 was
                       close to invisible on the cream page, so it is a step
                       darker without competing with the tray. */
                    <div className="flex items-center gap-2.5 mb-2.5 sm:mb-3 px-0.5">
                      <h4 className="text-xs sm:text-sm font-semibold text-gray-500 uppercase tracking-wider flex-shrink-0">
                        {t('Menu')} · {itemsNotOnToken.length}
                      </h4>
                      <div className="flex-1 h-px bg-gray-200" />
                    </div>
                  )}
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
                    {[...itemsNotOnToken].sort((a, b) => (b.soldToday || 0) - (a.soldToday || 0)).map((item) => (
                      <ItemCard
                        key={item._id}
                        item={item}
                        quantityOnToken={0}
                        onAddOrder={handleAddOrder}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
            )
          ) : (
            <div className="bg-white rounded-2xl border border-gray-100 sm:border-0 text-center py-10 sm:py-12 px-4">
              <div className="h-14 w-14 rounded-full bg-cafe-50 flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <ShoppingCart className="h-6 w-6 text-cafe-400" />
              </div>
              <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-1 sm:mb-2">
                {t('No menu items available')}
              </h3>
              <p className="text-sm sm:text-base text-gray-600">{t('Add some items to get started.')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
